import { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, Sparkles } from "lucide-react";
import { api } from "@/lib/api";
import PerfumeCard from "@/components/PerfumeCard";
import Reveal from "@/components/Reveal";
import Marquee from "@/components/Marquee";

const Brand = () => {
  const { brand } = useParams();
  const decoded = decodeURIComponent(brand || "");
  const [perfumes, setPerfumes] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api
      .get("/perfumes")
      .then((r) => setPerfumes(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const collection = useMemo(
    () => perfumes.filter((p) => p.brand.toLowerCase() === decoded.toLowerCase()),
    [perfumes, decoded]
  );

  const bestSellers = useMemo(
    () =>
      [...collection]
        .sort((a, b) => (b.featured - a.featured) || ((b.rating || 0) - (a.rating || 0)) || ((b.review_count || 0) - (a.review_count || 0)))
        .slice(0, 4),
    [collection]
  );

  const families = [...new Set(collection.map((p) => p.family))];
  const countBy = (g) => collection.filter((p) => p.gender === g).length;

  if (loading) return <div className="py-24 text-center text-sm text-[#5C5449]">Chargement…</div>;

  if (collection.length === 0)
    return (
      <div className="mx-auto max-w-3xl px-4 py-24 text-center">
        <p className="font-serif text-3xl text-[#1A1714]">Maison introuvable</p>
        <p className="mt-3 text-sm text-[#5C5449]">« {decoded} » n'est pas encore au catalogue ANM Paris.</p>
        <Link to="/boutique" className="mt-6 inline-block rounded-full bg-[#C59B27] px-8 py-3.5 text-sm font-semibold text-white transition-all hover:bg-[#A8821B] active:scale-95">
          Voir toute la boutique
        </Link>
      </div>
    );

  return (
    <div data-testid="brand-page">
      <section
        className="relative overflow-hidden px-4 py-20 text-center sm:px-8"
        style={{ background: "linear-gradient(135deg, #1A1714 0%, #2A241D 60%, #1A1714 100%)" }}
      >
        <div className="pointer-events-none absolute -left-24 top-0 h-72 w-72 rounded-full bg-[#C59B27]/15 blur-3xl" />
        <div className="pointer-events-none absolute -right-24 bottom-0 h-72 w-72 rounded-full bg-[#FF8A00]/10 blur-3xl" />
        <Reveal className="relative">
          <Link to="/boutique" className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.25em] text-[#C59B27] transition-colors hover:text-[#F5D576]">
            <ArrowLeft size={13} /> Toute la boutique
          </Link>
          <p className="mt-6 font-accent text-xs tracking-[0.4em] text-[#F7EEDB]/60">MAISON</p>
          <h1 className="gold-shimmer mt-3 font-serif text-4xl font-light tracking-tight sm:text-5xl lg:text-6xl">{decoded}</h1>
          <p className="mx-auto mt-4 max-w-xl text-sm leading-relaxed text-[#F7EEDB]/70 sm:text-base">
            {collection.length} création{collection.length > 1 ? "s" : ""} signée{collection.length > 1 ? "s" : ""} {decoded}, sélectionnée{collection.length > 1 ? "s" : ""} pour vous par ANM Paris.
          </p>
          <div className="mt-6 flex flex-wrap items-center justify-center gap-2">
            {countBy("Femme") > 0 && <span className="rounded-full border border-[#C59B27]/40 px-4 py-1.5 text-xs font-medium text-[#F7EEDB]">{countBy("Femme")} femme</span>}
            {countBy("Homme") > 0 && <span className="rounded-full border border-[#C59B27]/40 px-4 py-1.5 text-xs font-medium text-[#F7EEDB]">{countBy("Homme")} homme</span>}
            {countBy("Mixte") > 0 && <span className="rounded-full border border-[#C59B27]/40 px-4 py-1.5 text-xs font-medium text-[#F7EEDB]">{countBy("Mixte")} mixte</span>}
            {families.map((f) => (
              <span key={f} className="rounded-full bg-[#C59B27]/15 px-4 py-1.5 text-xs font-medium text-[#C59B27]">{f}</span>
            ))}
          </div>
        </Reveal>
      </section>

      <Marquee />

      <section className="mx-auto max-w-7xl px-4 py-14 sm:px-8">
        <Reveal>
          <p className="flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.25em] text-[#C59B27]">
            <Sparkles size={14} /> Les incontournables
          </p>
          <h2 className="mt-2 font-serif text-3xl text-[#1A1714]">Best-sellers {decoded}</h2>
        </Reveal>
        <div className="mt-8 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4" data-testid="brand-bestsellers">
          {bestSellers.map((p, i) => (
            <Reveal key={p.id} delay={i * 0.07}>
              <PerfumeCard p={p} />
            </Reveal>
          ))}
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-4 pb-20 sm:px-8">
        <Reveal>
          <h2 className="font-serif text-2xl text-[#1A1714] sm:text-3xl">Toute la collection {decoded}</h2>
        </Reveal>
        <div className="mt-8 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4" data-testid="brand-collection">
          {collection.map((p, i) => (
            <Reveal key={p.id} delay={Math.min(i, 6) * 0.05}>
              <PerfumeCard p={p} />
            </Reveal>
          ))}
        </div>
      </section>
    </div>
  );
};

export default Brand;
